import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {httpChangeOptionSms, HttpChangeOptionSmsOptions} from '@features/settings/http/settings.http.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {PhpUser, User} from '@features/user/types.ts'
import {captureException} from '@sentry/react'
import {errorHandler} from '@utilities/helpers.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {z} from 'zod'

const ResponseShape = PhpUser.pick({send_notifications_to_phone: true})
type ResponseShape = z.infer<typeof ResponseShape>

export const useChangeOptionSms = (
    options?: Omit<UseMutationOptions<ResponseShape, unknown, HttpChangeOptionSmsOptions['payload']>, 'mutationFn'>
) => {
    const queryClient = useQueryClient()
    return useMutation<ResponseShape, unknown, HttpChangeOptionSmsOptions['payload']>({
        ...options,
        mutationFn: payload =>
            parseAxiosPromise({
                axiosPromise: httpChangeOptionSms({payload}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        onMutate: async variables => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})
            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (data: User): User => ({
                    ...data,
                    sendNotificationsToPhone: variables.send_notifications_to_phone
                })
            )
        },
        onError: async (error, variables, context) => {
            errorHandler(error)
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})
            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (data: User): User => ({
                    ...data,
                    sendNotificationsToPhone: !data.sendNotificationsToPhone
                })
            )
            options?.onError?.(error, variables, context)
        }
    })
}
